import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  Cloud, Building2, Globe, Hash, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const Tenants = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'tenants');
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState({ total: 0, azure: 0, aws: 0, gcloud: 0, otros: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', cloud_provider: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'directory_name', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const pageSize = 10;

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, tenant: null });
  const [editModal, setEditModal] = useState({ open: false, tenant: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, tenant: null });
  const [formData, setFormData] = useState({
    directory_name: '', dominio: '', suscription: '', suscription_id: '',
    comentarios: '', cuit: '', razon_social: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/tenants/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.cloud_provider) params.append('cloud_provider', filters.cloud_provider);

      const response = await api.get(`/assets/tenants?${params}`);
      setTenants(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar tenants');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [page, filters.search, filters.cloud_provider, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Stat card click handler - filters by cloud provider
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPage(1);
    setSearchInput('');
    
    let cloudProvider = '';
    switch (filterType) {
      case 'azure':
        cloudProvider = 'AZURE';
        break;
      case 'aws':
        cloudProvider = 'AWS';
        break;
      case 'gcloud':
        cloudProvider = 'GCLOUD';
        break;
      case 'otros':
        cloudProvider = 'OTROS';
        break;
      default:
        cloudProvider = '';
        break;
    }
    
    setFilters(prev => ({ ...prev, search: '', cloud_provider: cloudProvider }));
  };

  // Cloud provider badge
  const getProviderBadge = (provider) => {
    switch (provider) {
      case 'AZURE':
        return <span className="badge badge-info">AZURE</span>;
      case 'AWS':
        return <span className="badge badge-warning">AWS</span>;
      case 'GCLOUD':
        return <span className="badge badge-error">GCLOUD</span>;
      default:
        return <span className="badge badge-outline">OTRO</span>;
    }
  };

  // View modal
  const handleView = (tenant) => {
    setViewModal({ open: true, tenant });
  };

  // Edit modal
  const handleEdit = (tenant) => {
    setFormData({
      directory_name: tenant.directory_name || '',
      dominio: tenant.dominio || '',
      suscription: tenant.suscription || '',
      suscription_id: tenant.suscription_id || '',
      comentarios: tenant.comentarios || '',
      cuit: tenant.cuit || '',
      razon_social: tenant.razon_social || ''
    });
    setEditModal({ open: true, tenant, isCopy: false });
  };

  // Copy modal
  const handleCopy = (tenant) => {
    setFormData({
      directory_name: '', // Unique - clear
      dominio: '', // Unique - clear
      suscription: tenant.suscription || '', // Copy
      suscription_id: '', // Unique - clear
      comentarios: tenant.comentarios || '', // Copy
      cuit: '', // Unique - clear
      razon_social: tenant.razon_social || '' // Copy
    });
    setEditModal({ open: true, tenant: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      directory_name: '', dominio: '', suscription: '', suscription_id: '',
      comentarios: '', cuit: '', razon_social: ''
    });
    setEditModal({ open: true, tenant: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.directory_name.trim()) {
      toast.error('El nombre del directorio es requerido');
      return;
    }
    setSaving(true);
    try {
      if (editModal.tenant && !editModal.isCopy) {
        await api.put(`/assets/tenants/${editModal.tenant.id}`, formData);
        toast.success('Tenant actualizado exitosamente');
      } else {
        await api.post('/assets/tenants', formData);
        toast.success('Tenant creado exitosamente');
      }
      setEditModal({ open: false, tenant: null, isCopy: false });
      fetchTenants();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/tenants/${deleteModal.tenant.id}`);
      toast.success('Tenant eliminado exitosamente');
      setDeleteModal({ open: false, tenant: null });
      fetchTenants();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Tenants</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Tenants M365/Azure</h1>
            <p className="page-subtitle">Gestion de tenants cloud Microsoft, AWS y Google</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-tenant-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Tenant
            </Button>
          )}
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="tenant-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Cloud className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total Tenants</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${activeStatFilter === 'azure' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleStatFilter('azure')}
            data-testid="stats-azure"
          >
            <Cloud className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.azure}</div>
              <div className="stats-card-label">Azure / M365</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-orange-500/50 ${activeStatFilter === 'aws' ? 'ring-2 ring-orange-500' : ''}`}
            onClick={() => handleStatFilter('aws')}
            data-testid="stats-aws"
          >
            <Cloud className="h-6 w-6 text-orange-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-orange-500">{stats.aws}</div>
              <div className="stats-card-label">AWS</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${activeStatFilter === 'gcloud' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('gcloud')}
            data-testid="stats-gcloud"
          >
            <Cloud className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.gcloud}</div>
              <div className="stats-card-label">Google Cloud</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-gray-500/50 ${activeStatFilter === 'otros' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => handleStatFilter('otros')}
            data-testid="stats-otros"
          >
            <Cloud className="h-6 w-6 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-gray-500">{stats.otros}</div>
              <div className="stats-card-label">Otros</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('directory_name')}
                        >
                          <div className="flex items-center gap-1">
                            DIRECTORIO
                            <SortIcon field="directory_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('dominio')}
                        >
                          <div className="flex items-center gap-1">
                            DOMINIO
                            <SortIcon field="dominio" />
                          </div>
                        </th>
                        <th className="text-center">CLOUD</th>
                        <th>CUIT</th>
                        <th>RAZON SOCIAL</th>
                        <th className="w-28 text-center">ACCIONES</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.cloud_provider || undefined}
                            onValueChange={(v) => handleFilterChange('cloud_provider', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              <SelectItem value="AZURE">Azure</SelectItem>
                              <SelectItem value="AWS">AWS</SelectItem>
                              <SelectItem value="GCLOUD">GCloud</SelectItem>
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.length === 0 ? (
                        <tr>
                          <td colSpan={6}>
                            <div className="empty-state">
                              <Cloud className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron tenants</p>
                            </div>
                          </td>
                        </tr>
                      ) : tenants.map((tenant) => (
                        <tr key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                          <td className="font-medium text-primary">{tenant.directory_name}</td>
                          <td className="text-muted-foreground font-mono text-sm">{tenant.dominio || '-'}</td>
                          <td className="text-center">{getProviderBadge(tenant.cloud_provider)}</td>
                          <td className="text-muted-foreground">{tenant.cuit || '-'}</td>
                          <td className="text-muted-foreground max-w-[200px] truncate">{tenant.razon_social || '-'}</td>
                          <td className="w-28">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(tenant)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-tenant-${tenant.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(tenant)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-tenant-${tenant.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(tenant)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-tenant-${tenant.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, tenant })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-tenant-${tenant.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, tenant: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5 text-primary" />
                {viewModal.tenant?.directory_name}
              </DialogTitle>
            </DialogHeader>
            {viewModal.tenant && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Directorio</Label>
                    <p className="font-medium">{viewModal.tenant.directory_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Dominio</Label>
                    <p className="font-mono text-sm">{viewModal.tenant.dominio || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suscripcion</Label>
                    <div className="mt-1">{getProviderBadge(viewModal.tenant.cloud_provider)}</div>
                    <p className="text-sm text-muted-foreground mt-1">{viewModal.tenant.suscription || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suscription ID</Label>
                    <p className="font-mono text-xs break-all">{viewModal.tenant.suscription_id || '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">CUIT</Label>
                    <p className="font-mono">{viewModal.tenant.cuit || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Razon Social</Label>
                    <p className="text-sm">{viewModal.tenant.razon_social || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Comentarios</Label>
                    <p className="text-sm text-muted-foreground">{viewModal.tenant.comentarios || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, tenant: null, isCopy: false })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar Tenant' : editModal.tenant ? 'Editar Tenant' : 'Nuevo Tenant'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Unique fields - highlighted in copy mode */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Directorio *</Label>
                <Input
                  value={formData.directory_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, directory_name: e.target.value }))}
                  placeholder="Nombre del tenant"
                  data-testid="input-directory"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico - debe ser diferente</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Dominio</Label>
                <Input
                  value={formData.dominio}
                  onChange={(e) => setFormData(prev => ({ ...prev, dominio: e.target.value }))}
                  placeholder="@example.onmicrosoft.com"
                  data-testid="input-dominio"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Suscripcion</Label>
                <Input
                  value={formData.suscription}
                  onChange={(e) => setFormData(prev => ({ ...prev, suscription: e.target.value }))}
                  placeholder="AZURE, M365, AWS, GCLOUD..."
                  data-testid="input-suscription"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado del original</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Suscription ID</Label>
                <Input
                  value={formData.suscription_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, suscription_id: e.target.value }))}
                  placeholder="GUID de suscripcion"
                  data-testid="input-suscription-id"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">CUIT</Label>
                <Input
                  value={formData.cuit}
                  onChange={(e) => setFormData(prev => ({ ...prev, cuit: e.target.value }))}
                  placeholder="30-12345678-9"
                  data-testid="input-cuit"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Razon Social</Label>
                <Input
                  value={formData.razon_social}
                  onChange={(e) => setFormData(prev => ({ ...prev, razon_social: e.target.value }))}
                  placeholder="Razon social de la empresa"
                  data-testid="input-razon-social"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado del original</p>}
              </div>
              
              <div className={`col-span-2 space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Comentarios</Label>
                <Textarea
                  value={formData.comentarios}
                  onChange={(e) => setFormData(prev => ({ ...prev, comentarios: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={3}
                  data-testid="input-comentarios"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado del original</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, tenant: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, tenant: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Tenant
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              ¿Estas seguro que deseas eliminar el tenant <strong>{deleteModal.tenant?.directory_name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, tenant: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Tenants;
